import { map, reduce } from 'ramda'

import { Report } from '@driftt/api-types'

import {
  ConversationFeedItem,
  LeadsQualified,
  SimpleReportRow,
  TopHitsRow,
} from 'api/report/realTime/types'

import { parseRepPerformanceTable } from '../conversationSummary/parsers'
import {
  IConversationLiveChatRating,
  IMissedChatsCount,
  ITicketsCreatedCount,
} from '../types'

export const parseCSATByConversation = (response) =>
  reduce(
    (acc: ConversationFeedItem[], row: TopHitsRow) => {
      const { dimensions, metrics } = row
      const convos = metrics[0].map((convoId) => ({
        id: convoId,
        rating: dimensions[0],
        type: 'csat',
      }))
      return acc.concat(convos)
    },
    [],
    response.data.rows
  )

export const parseRealTimeRepPerformanceTable = parseRepPerformanceTable

export const parseConversationRatingsByRep = (
  response: Report
): IConversationLiveChatRating[] => {
  return map((row) => {
    const agentId = row.dimensions[0] as number
    const liveChatRating = row.metrics[1].toFixed(1)
    return {
      agentId,
      liveChatRating,
    }
  }, response.data.rows)
}

export const parseTicketsCreatedByRep = (response: ITicketsCreatedCount[]) => {
  return reduce(
    (acc, row) => {
      const { agentId, ticketsCreated } = row
      acc[agentId] = ticketsCreated
      return acc
    },
    {},
    response
  )
}

export const parseOpenAndClosedCreatedByRep = (response) => {
  return reduce(
    (acc, row: SimpleReportRow) => {
      const { dimensions, metrics } = row
      const agentId = dimensions[0]
      const status = dimensions[1]
      const numOfConvos = metrics[0]
      acc[agentId] = acc[agentId] || {}
      if (status) {
        acc[agentId][status] = numOfConvos
      }
      return acc
    },
    {},
    response.data.rows
  )
}

export const parseMissedChatsByType = (response) => {
  return reduce(
    (acc: ConversationFeedItem[], row: TopHitsRow) => {
      const { dimensions, metrics } = row
      const convos = metrics[0].map((convoId) => ({
        id: convoId,
        reason: dimensions[0],
        type: 'missed',
      }))
      return acc.concat(convos)
    },
    [],
    response.data.rows
  )
}

export const parseMissedChatsByAgent = (
  response
): Record<string, IMissedChatsCount> => {
  return reduce(
    (acc, row: SimpleReportRow) => {
      const { dimensions, metrics } = row
      const agentId = dimensions[0]
      const actionType = dimensions[1]
      const missedChats = metrics[0]
      //set defaults if they don't exist yet
      acc[agentId] = acc[agentId] || {}
      acc[agentId]['TOTAL'] = acc[agentId]['TOTAL'] || 0
      //update with response data
      if (actionType) {
        acc[agentId][actionType] = missedChats
      } else {
        acc[agentId]['NONE'] = missedChats
      }
      acc[agentId]['TOTAL'] += missedChats

      return acc
    },
    {},
    response.data.rows
  )
}

export const parseAsKeyValueObject = (response) => {
  return reduce(
    (acc, row: SimpleReportRow) => {
      const { dimensions, metrics } = row
      acc[dimensions[0]] = metrics[0]
      return acc
    },
    {},
    response.data.rows
  )
}

export const parseLeadsQualifiedByAgent = (response): LeadsQualified => {
  return reduce(
    (acc, row: SimpleReportRow) => {
      const { dimensions, metrics } = row
      const [agentId, agentIsParticipant] = dimensions
      acc[agentId] = {
        isParticipantCount: acc[agentId]?.isParticipantCount ?? 0,
        otherCount: acc[agentId]?.otherCount ?? 0,
        total: acc[agentId]?.total ?? 0,
      }
      if (agentIsParticipant) {
        acc[agentId].isParticipantCount += metrics[0]
        acc[agentId].total += metrics[0]
      } else if (agentIsParticipant !== undefined && !agentIsParticipant) {
        acc[agentId].otherCount += metrics[0]
        acc[agentId].total += metrics[0]
      }
      return acc
    },
    {},
    response.data.rows
  )
}
